import type { RecurrenceOverride, RecurrenceRule, Task } from "@app/core";
import { addDays, endOfDay, fromIso, startOfDay, tasksOnDay } from "@app/core";

import { expandTasksWindowed, type RuleExpansionCache } from "../calendar-view/build-days.js";

/** Days shown in the agenda: today + the following six. */
export const AGENDA_DAYS = 7;
/** How far back missed recurring occurrences are surfaced as overdue. Real
 *  (non-recurring) overdue tasks have no age limit — only series occurrences
 *  are capped, so an abandoned daily series can't flood the section. */
export const OVERDUE_LOOKBACK_DAYS = 14;

export interface AgendaDay {
  date: Date;
  items: Task[];
}

export interface Agenda {
  /** Open past-due tasks (never events), oldest first: real tasks of any age
   *  plus recurring occurrences missed within the lookback window. */
  overdue: Task[];
  /** Snoozed tasks/occurrences awaiting their wake time, soonest wake first.
   *  Dropped from `overdue` while snoozed; surfaced in a "Snoozed" group so the
   *  user can find and wake them early. */
  snoozed: Task[];
  /** Timed minutes that overlap today, including done/snoozed reservations and
   *  blocks that started before midnight. */
  scheduledMinutes: number;
  /** Running timed item (including an overnight continuation), otherwise the
   *  next timed item that starts today. */
  nextUp: NextUp | null;
  /** Today + the following `days - 1` days ({@link AGENDA_DAYS} by default; the
   *  `agendaDays` setting picks 3/7/14). Items sorted all-day first, then by
   *  start time. Done items stay (rendered struck-through). */
  days: AgendaDay[];
}

/** Today always renders an empty-state section. Empty future days only have a
 * scroll target when the user chose to show them. */
export function agendaDayNavigable(index: number, itemCount: number, showEmptyDays: boolean): boolean {
  return index === 0 || itemCount > 0 || showEmptyDays;
}

/**
 * Builds the Today/agenda model from the FILTERED task list (callers apply
 * `useViewFilters` first, like the calendar routes). Expands recurrence over
 * the agenda window so series show their actual occurrences — never the frozen
 * base task, which would otherwise sit in "Overdue" forever.
 */
export function buildAgenda(
  tasks: Task[],
  rules: Record<string, RecurrenceRule>,
  overrides: Record<string, ReadonlyArray<RecurrenceOverride>>,
  now: Date,
  cache?: RuleExpansionCache,
  agendaDays: number = AGENDA_DAYS,
): Agenda {
  const todayStart = startOfDay(now);
  const from = startOfDay(addDays(now, -OVERDUE_LOOKBACK_DAYS));
  const to = endOfDay(addDays(now, agendaDays - 1));
  const expanded = expandTasksWindowed(tasks, rules, overrides, from, to, cache);

  // Real overdue tasks can be arbitrarily old, so gather starts before the
  // expansion window from the raw list. Long all-day spans can also intersect
  // the window; recentIds prevents those real tasks from being listed twice.
  const recent = expanded.filter((t) => isOverdue(t, now));
  const recentIds = new Set(recent.map((t) => t.id));
  const ancient = tasks.filter(
    (t) => !t.deleted_at && !t.recurrence_id && t.due_at && fromIso(t.due_at) < from
      && !recentIds.has(t.id) && isOverdue(t, now),
  );

  // Snoozed real tasks (any age) + snoozed occurrences from the window.
  const snoozedReal = tasks.filter((t) => !t.deleted_at && !t.recurrence_id && t.status === "snoozed");
  const snoozedOcc = allSnoozedOccurrences(tasks, rules, overrides, expanded);
  const snoozed = [...snoozedReal, ...snoozedOcc].sort((a, b) => {
    const byWake = compareInstants(a.snoozed_until ?? a.due_at, b.snoozed_until ?? b.due_at);
    return byWake !== 0 ? byWake : a.id.localeCompare(b.id);
  });

  const days: AgendaDay[] = Array.from({ length: agendaDays }, (_, i) => {
    const date = addDays(todayStart, i);
    // Snoozed items live in their own group (+ the calendar grid), not the day list.
    return { date, items: sortAgendaItems(tasksOnDay(expanded, date).filter((t) => t.status !== "snoozed")) };
  });

  // The day list deliberately assigns timed work to its START day and moves
  // snoozed rows into their own group. The hero's scheduled-load total has a
  // different contract: calendar reservations of any status count by their
  // actual overlap with today, including an overnight continuation.
  const todayTimedSpans = expandTasksWindowed(
    tasks, rules, overrides, todayStart, endOfDay(todayStart), cache, true,
  );
  const scheduledToday = scheduledMinutes(todayTimedSpans, todayStart);

  return {
    overdue: sortOverdueByAge([...ancient, ...recent]),
    snoozed,
    scheduledMinutes: scheduledToday,
    nextUp: nextUp(todayTimedSpans, now),
    days,
  };
}

/** Snoozed recurring occurrences remain discoverable even when their original
 * slot sits outside the configurable agenda/lookback window. Expand only the
 * missing single-override refs, rather than widening every series across an
 * arbitrarily large range. */
function allSnoozedOccurrences(
  tasks: Task[],
  rules: Record<string, RecurrenceRule>,
  overrides: Record<string, ReadonlyArray<RecurrenceOverride>>,
  expanded: Task[],
): Task[] {
  const result = expanded.filter((task) => task.virtual && task.status === "snoozed");
  const seen = new Set(result.map((task) => task.id));
  const sourceByRule = new Map(
    tasks
      .filter((task) => !task.deleted_at && task.recurrence_id)
      .map((task) => [task.recurrence_id!, task] as const),
  );

  for (const [ruleId, list] of Object.entries(overrides)) {
    const source = sourceByRule.get(ruleId);
    const rule = rules[ruleId];
    if (!source || !rule) continue;
    for (const override of list) {
      if (override.range !== "single" || override.status !== "snoozed") continue;
      const expectedId = `${source.id}::v::${override.recurrence_id_ref}`;
      if (seen.has(expectedId)) continue;
      const occurrenceAt = fromIso(override.recurrence_id_ref);
      if (!Number.isFinite(occurrenceAt.getTime())) continue;
      const occurrence = expandTasksWindowed(
        [source], rules, { [ruleId]: list }, occurrenceAt, occurrenceAt,
      ).find((task) => task.id === expectedId && task.status === "snoozed");
      if (occurrence) {
        seen.add(occurrence.id);
        result.push(occurrence);
      }
    }
  }
  return result;
}

/** "Oldest first" means time since the occupied slot ended, not earliest
 * start. This matters for multi-day all-day work. */
function sortOverdueByAge(tasks: Task[]): Task[] {
  return [...tasks].sort((a, b) => {
    const byEnd = taskSlotEnd(a)!.getTime() - taskSlotEnd(b)!.getTime();
    if (byEnd !== 0) return byEnd;
    const byDue = (a.due_at ?? "").localeCompare(b.due_at ?? "");
    return byDue !== 0 ? byDue : a.id.localeCompare(b.id);
  });
}

/** End of the task's occupied slot. All-day durations are logical day units;
 *  timed durations are elapsed minutes from the start instant. */
export function taskSlotEnd(t: Task): Date | null {
  if (!t.due_at) return null;
  const due = fromIso(t.due_at);
  if (t.all_day) {
    const days = Math.max(1, Math.ceil(Math.max(0, t.duration_minutes) / 1440));
    return endOfDay(addDays(startOfDay(due), days - 1));
  }
  return new Date(due.getTime() + Math.max(0, t.duration_minutes) * 60_000);
}

/** An open, non-event task becomes overdue only after its complete slot ends. */
export function isOverdue(t: Task, now: Date): boolean {
  if (t.is_event || t.status !== "open" || !t.due_at) return false;
  return taskSlotEnd(t)!.getTime() <= now.getTime();
}

/** Whole local calendar days since the task's due slot ended. Multi-day
 *  all-day tasks age from their LAST occupied day, not their start date. */
export function overdueAgeDays(t: Task, now: Date): number {
  const end = taskSlotEnd(t);
  if (!end) return 0;
  const ordinal = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000;
  return Math.max(0, ordinal(now) - ordinal(end));
}

/** Sum the part of each timed slot that actually overlaps `day` — tasks and
 *  events of any status. Clipping prevents an overnight/multi-day block from
 *  claiming its full duration on every touched day. */
export function scheduledMinutes(items: Task[], day: Date): number {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = startOfDay(addDays(day, 1)).getTime();
  const total = items.reduce((sum, task) => {
    if (task.all_day || !task.due_at) return sum;
    const start = fromIso(task.due_at).getTime();
    const end = taskSlotEnd(task)?.getTime() ?? start;
    const overlap = Math.max(0, Math.min(end, dayEnd) - Math.max(start, dayStart));
    return sum + overlap / 60_000;
  }, 0);
  return Math.round(total);
}

/** Target instant for a bulk "move to today" operation. Preserve a timed
 * task's wall-clock time while that would leave it running/upcoming; otherwise
 * move it to the next five-minute boundary so the operation actually clears
 * it from Overdue. All-day tasks stay all-day at today's local midnight. */
export function dueWhenMovedToToday(task: Task, now: Date): Date {
  const due = startOfDay(now);
  if (task.all_day) return due;

  if (task.due_at) {
    const previous = fromIso(task.due_at);
    due.setHours(previous.getHours(), previous.getMinutes(), 0, 0);
    const end = due.getTime() + Math.max(0, task.duration_minutes) * 60_000;
    if (end > now.getTime()) return due;
  }

  const step = 5 * 60_000;
  // The next global five-minute boundary can be tomorrow late at night. Keep
  // the promise made by "move to today" by clamping to today's final instant;
  // even a zero-duration task then remains open for the rest of this day.
  return new Date(Math.min(
    (Math.floor(now.getTime() / step) + 1) * step,
    endOfDay(now).getTime(),
  ));
}

/** Compact glance at a day (the hero's evening "Tomorrow: …" line). */
export interface DayPreview {
  /** Not-yet-done items, events included. */
  open: number;
  /** Start of the earliest open timed item, or null when the day has none. */
  firstStart: Date | null;
}

export function dayPreview(items: Task[]): DayPreview {
  const open = items.filter((t) => t.status !== "done");
  const starts = open
    .filter((t) => !t.all_day && t.due_at)
    .map((t) => fromIso(t.due_at!))
    .sort((a, b) => a.getTime() - b.getTime());
  return { open: open.length, firstStart: starts[0] ?? null };
}

/** Where the "now" line sits in a day's sorted item list: the index of the
 *  first timed item still ahead. -1 when the line wouldn't separate anything
 *  (nothing timed has started yet, or nothing timed is left ahead). */
export function nowLineIndex(items: Task[], now: Date): number {
  const timed = (t: Task) => !t.all_day && !!t.due_at;
  const ahead = (t: Task) => timed(t) && fromIso(t.due_at!) > now;
  if (!items.some((t) => timed(t) && !ahead(t)) || !items.some(ahead)) return -1;
  return items.findIndex(ahead);
}

/** A timed item whose whole slot has passed (end ≤ now) without being
 *  completed — the today list dims these so the remaining plan stands out. */
export function isPastSlot(t: Task, now: Date): boolean {
  if (t.all_day || !t.due_at || t.status !== "open") return false;
  return taskSlotEnd(t)!.getTime() <= now.getTime();
}

/** Completion progress over a day's items. Events aren't completable, so only
 *  tasks count toward the ring/bars. */
export interface DayProgress {
  total: number;
  done: number;
}

export function dayProgress(items: Task[]): DayProgress {
  const tasks = items.filter((t) => !t.is_event);
  return { total: tasks.length, done: tasks.filter((t) => t.status === "done").length };
}

export interface NextUp {
  task: Task;
  /** "now" = started and not yet ended (needs a duration); "next" = still ahead. */
  kind: "now" | "next";
  start: Date;
  end: Date;
}

/**
 * The Today spotlight pick: a TIMED open item currently running (the one ending
 * soonest wins), else the next timed open item still ahead today. All-day,
 * done and snoozed items never surface.
 */
export function nextUp(todayItems: Task[], now: Date): NextUp | null {
  const timed = todayItems
    .filter((t) => !t.all_day && t.due_at && t.status === "open")
    .map((t) => {
      const start = fromIso(t.due_at!);
      return { task: t, start, end: taskSlotEnd(t)! };
    });
  const running = timed
    .filter((x) => x.start <= now && now < x.end)
    .sort((a, b) => a.end.getTime() - b.end.getTime());
  if (running[0]) return { ...running[0], kind: "now" };
  const ahead = timed.filter((x) => x.start > now).sort((a, b) => a.start.getTime() - b.start.getTime());
  return ahead[0] ? { ...ahead[0], kind: "next" } : null;
}

/** "Today · Jun 9" / "Tomorrow · Jun 10" / "Thursday · Jun 11". Relative
 * labels are injected so this pure formatter never owns translated UI copy. */
export function agendaDayLabel(
  date: Date,
  now: Date,
  locale: string,
  relative: { today: string; tomorrow: string },
): string {
  const diff = Math.round((startOfDay(date).getTime() - startOfDay(now).getTime()) / 86_400_000);
  const name =
    diff === 0 ? relative.today : diff === 1 ? relative.tomorrow : date.toLocaleDateString(locale, { weekday: "long" });
  return `${name} · ${date.toLocaleDateString(locale, { month: "short", day: "numeric" })}`;
}

function sortAgendaItems(items: Task[]): Task[] {
  return [...items].sort((a, b) => {
    if (a.all_day !== b.all_day) return a.all_day ? -1 : 1;
    // ISO-8601 permits offsets. Lexical order is only chronological when both
    // strings use the same offset/spelling; AI/imported entries may use +hh:mm
    // while app-created entries use Z.
    const cmp = compareInstants(a.due_at, b.due_at);
    return cmp !== 0 ? cmp : a.title.localeCompare(b.title);
  });
}

function compareInstants(a: string | null, b: string | null): number {
  const aMs = a ? fromIso(a).getTime() : Number.POSITIVE_INFINITY;
  const bMs = b ? fromIso(b).getTime() : Number.POSITIVE_INFINITY;
  const safeA = Number.isFinite(aMs) ? aMs : Number.POSITIVE_INFINITY;
  const safeB = Number.isFinite(bMs) ? bMs : Number.POSITIVE_INFINITY;
  return safeA === safeB ? 0 : safeA < safeB ? -1 : 1;
}
